import { useMemo } from 'react';
import { useFormikContext } from 'formik';
import { CollectionOrderStates } from 'shared-pages/common/constants';
import { useRootStore } from 'store';
import { isPackagingStepNeeded } from './is-packaging-step-needed';

/** Tab event keys matching ApprovalTabs in order-approval-tabs.tsx */
export const APPROVAL_TAB_KEYS = {
  Request: '1',
  Organize: '2',
  Packing: '3',
  Collector: '4',
  ActionHistory: '5',
} as const;

export type ApprovalTabKey = (typeof APPROVAL_TAB_KEYS)[keyof typeof APPROVAL_TAB_KEYS];

/**
 * Default tab to activate when statusId changes. Uses only workflow tabs (not ActionHistory).
 * Each status maps to the tab that is most relevant for that workflow step.
 */
function getDefaultTabKeyForStatus(
  statusId: number,
  packagingStepIsNeeded: boolean,
): Exclude<ApprovalTabKey, typeof APPROVAL_TAB_KEYS.ActionHistory> {
  switch (statusId) {
    case CollectionOrderStates.TemporarySaved:
      return APPROVAL_TAB_KEYS.Request;
    case CollectionOrderStates.ApplicationSubmitted:
    case CollectionOrderStates.ApplicationRejected:
      return APPROVAL_TAB_KEYS.Organize;
    case CollectionOrderStates.OrganizationCompleted:
      return packagingStepIsNeeded === true
        ? APPROVAL_TAB_KEYS.Packing
        : APPROVAL_TAB_KEYS.Collector;
    case CollectionOrderStates.PackageShipmentCompleted:
    case CollectionOrderStates.CollectorOrder:
    case CollectionOrderStates.ScheduledCollectorCollection:
      return APPROVAL_TAB_KEYS.Collector;
    default:
      return APPROVAL_TAB_KEYS.Request;
  }
}

/**
 * Returns the tab key that should be active by default for the current statusId.
 * Used to switch to a specific tab when the user or workflow changes status.
 */
export function useDefaultTabKeyForStatus(): Exclude<
  ApprovalTabKey,
  typeof APPROVAL_TAB_KEYS.ActionHistory
> {
  const { values } = useFormikContext<{
    statusId?: number;
    xrbData?: {
      appropriatePackaging?: boolean;
      vermiculiteUsed?: boolean;
      aeroplastUsed?: boolean;
      plasticBagUsed?: boolean;
      polyethyleneUsed?: boolean;
    };
  }>();
  const statusId = values?.statusId ?? CollectionOrderStates.TemporarySaved;
  const packagingStepIsNeeded = isPackagingStepNeeded(values?.xrbData);
  const returnValue = getDefaultTabKeyForStatus(statusId, packagingStepIsNeeded);
  //console.log('$$$ useDefaultTabKeyForStatus', statusId, packagingStepIsNeeded, returnValue);
  return returnValue;
}

/**
 * Returns a map of tab key -> visible (true/false) based on statusId and packagingStepIsNeeded.
 * Uses Formik context for statusId and values.xrbData.packagingStepIsNeeded.
 * Organize tab is hidden when packagingStepIsNeeded === true.
 */
export function useTabVisibleByStatus(): Record<ApprovalTabKey, boolean> {
  const { appStore } = useRootStore();
  const { values } = useFormikContext<{
    statusId?: number;
    xrbData?: {
      appropriatePackaging?: boolean;
      vermiculiteUsed?: boolean;
      aeroplastUsed?: boolean;
      plasticBagUsed?: boolean;
      polyethyleneUsed?: boolean;
    };
  }>();
  const statusId = values?.statusId ?? CollectionOrderStates.TemporarySaved;
  const xrbData = values?.xrbData ?? {};
  const packagingStepIsNeeded = isPackagingStepNeeded(xrbData);

  const tabAllowedByUserType = useMemo((): Record<ApprovalTabKey, boolean> => {
    const isPlatformOwner = appStore.isPlatformOwner;
    const isCollector = appStore.isCollector || appStore.isCollectorDriver;
    const isCollectionPoint = appStore.isCollectionPoint;
    const isContributor = appStore.isContributor;

    return {
      [APPROVAL_TAB_KEYS.Request]: true,
      [APPROVAL_TAB_KEYS.Organize]: isPlatformOwner || isCollector,
      [APPROVAL_TAB_KEYS.Packing]: isPlatformOwner || isCollector,
      [APPROVAL_TAB_KEYS.Collector]: isPlatformOwner || isCollector,
      [APPROVAL_TAB_KEYS.ActionHistory]: isPlatformOwner,
    };
  }, [
    appStore.isPlatformOwner,
    appStore.isCollector,
    appStore.isCollectorDriver,
    appStore.isCollectionPoint,
    appStore.isContributor,
  ]);

  /**
   * For each statusId, the tabs that are visible (before packagingStepIsNeeded exceptions).
   * Add or remove tab keys per status to control visibility by workflow step.
   * When packagingStepIsNeeded === true: Organize and Packing are hidden; Collector only for statuses 7, 8, 9.
   */
  const visibleTabsByStatus = useMemo(
    (): Record<number, ApprovalTabKey[]> => ({
      [CollectionOrderStates.TemporarySaved]: [
        APPROVAL_TAB_KEYS.Request,
        APPROVAL_TAB_KEYS.ActionHistory,
      ],
      [CollectionOrderStates.ApplicationSubmitted]: [
        APPROVAL_TAB_KEYS.Request,
        APPROVAL_TAB_KEYS.Organize,
        APPROVAL_TAB_KEYS.ActionHistory,
      ],
      [CollectionOrderStates.ApplicationRejected]: [
        APPROVAL_TAB_KEYS.Request,
        APPROVAL_TAB_KEYS.Organize,
        APPROVAL_TAB_KEYS.ActionHistory,
      ],
      [CollectionOrderStates.OrganizationCompleted]: [
        APPROVAL_TAB_KEYS.Request,
        APPROVAL_TAB_KEYS.Organize,
        ...(packagingStepIsNeeded ? [APPROVAL_TAB_KEYS.Packing] : [APPROVAL_TAB_KEYS.Collector]),
        APPROVAL_TAB_KEYS.ActionHistory,
      ],
      [CollectionOrderStates.PackageShipmentCompleted]: [
        APPROVAL_TAB_KEYS.Request,
        APPROVAL_TAB_KEYS.Organize,
        APPROVAL_TAB_KEYS.Packing,
        APPROVAL_TAB_KEYS.Collector,
        APPROVAL_TAB_KEYS.ActionHistory,
      ],
      [CollectionOrderStates.CollectorOrder]: [
        APPROVAL_TAB_KEYS.Request,
        APPROVAL_TAB_KEYS.Organize,
        ...(packagingStepIsNeeded ? [APPROVAL_TAB_KEYS.Packing] : []),
        APPROVAL_TAB_KEYS.Collector,
        APPROVAL_TAB_KEYS.ActionHistory,
      ],
      [CollectionOrderStates.ScheduledCollectorCollection]: [
        APPROVAL_TAB_KEYS.Request,
        APPROVAL_TAB_KEYS.Organize,
        ...(packagingStepIsNeeded ? [APPROVAL_TAB_KEYS.Packing] : []),
        APPROVAL_TAB_KEYS.Collector,
        APPROVAL_TAB_KEYS.ActionHistory,
      ],
      [CollectionOrderStates.Executed]: [
        APPROVAL_TAB_KEYS.Request,
        APPROVAL_TAB_KEYS.Organize,
        ...(packagingStepIsNeeded ? [APPROVAL_TAB_KEYS.Packing] : []),
        APPROVAL_TAB_KEYS.Collector,
        APPROVAL_TAB_KEYS.ActionHistory,
      ],
    }),
    [packagingStepIsNeeded],
  );

  return useMemo(() => {
    let visibleTabs = visibleTabsByStatus[statusId] ?? [APPROVAL_TAB_KEYS.Request];

    // Apply role-based visibility on top of workflow visibility.
    visibleTabs = visibleTabs.filter((tabKey) => tabAllowedByUserType[tabKey] === true);

    const out = {} as Record<ApprovalTabKey, boolean>;
    for (const tabKey of Object.values(APPROVAL_TAB_KEYS)) {
      out[tabKey] = visibleTabs.includes(tabKey);
    }
    return out;
  }, [statusId, packagingStepIsNeeded, visibleTabsByStatus, tabAllowedByUserType]);
}
